import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'

// Api
import { downloadPayment } from 'api/payment'

// Models
import PaymentBreakdownModel from 'models/PaymentBreakdown'

// Helpers
import { resolveErrorMessage } from 'helpers'

// Services
import message from 'services/message'

// Compoment
import Box from 'ui/Box'
import Loading from 'ui/Loading'
import Heading from 'ui/Heading'
import ButtonDownload from 'ui/ButtonDownload'
import PaymentPOP from 'components/Payment/PaymentPOP'

const propTypes = {
  payment: PropTypes.instanceOf(PaymentBreakdownModel),
}

const defaultProps = {
  payment: new PaymentBreakdownModel(),
}

export const PaymentBreakdown = ({ payment }) => {
  // States
  const [loading, setLoading] = useState(false)

  const onDownloadPaymentBreakdownClick = async (typeFile) => {
    try {
      setLoading(true)
      return await downloadPayment({
        paymentId: payment.id,
        paymentMethod: payment.paymentMethod,
        typeFile,
      })
    } catch (error) {
      message.error(await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box style={{ borderRadius: 0 }}>
      <Loading spinning={loading}>
        <Heading size={18} paddingBottom={20} paddingTop={15}>
          Payment breakdown
        </Heading>

        <table style={{ marginBottom: '30px' }} cellSpacing="8">
          <tbody>
            {/* Row 1 */}
            <tr style={{ verticalAlign: 'top' }}>
              <td>Lessor name:</td>
              <td id="lessorName">{payment.lessorName}</td>
            </tr>
            <tr style={{ verticalAlign: 'top' }}>
              <td style={{ paddingBottom: '36px' }}>Vendor code:</td>
              <td id="vendorCode">{payment.vendorCode}</td>
            </tr>

            {/* Row 2 */}
            <tr style={{ verticalAlign: 'top' }}>
              <td>Posting date:</td>
              <td id="paymentDate">{payment.createdAt}</td>
            </tr>
            <tr style={{ verticalAlign: 'top' }}>
              <td>Date processed:</td>
              <td id="processedDate">{payment.processedDate}</td>
            </tr>
            <tr style={{ verticalAlign: 'top' }}>
              <td style={{ paddingBottom: '36px' }}>Payment period:</td>
              <td id="paymentPeriod">
                {`${payment.paymentStartDate} - ${payment.paymentEndDate}`}
              </td>
            </tr>

            {/* Row 3 */}
            <tr style={{ verticalAlign: 'top' }}>
              <td>Payment method:</td>
              <td id="paymentMethod">{payment.method}</td>
            </tr>

            {payment.isBank && (
              <Fragment>
                <tr style={{ verticalAlign: 'top' }}>
                  <td>Beneficiary bank name:</td>
                  <td id="beneficiaryBankName">
                    {payment.beneficiaryBankName}
                  </td>
                </tr>

                <tr style={{ verticalAlign: 'top' }}>
                  <td style={{ paddingBottom: '36px' }}>
                    Beneficiary Bank account no.
                  </td>
                  <td id="beneficiaryBankAccountNo">
                    {payment.beneficiaryBankAccountNo}
                  </td>
                </tr>
              </Fragment>
            )}

            {payment.isGCash && (
              <Fragment>
                <tr style={{ verticalAlign: 'top' }}>
                  <td>Reference number:</td>
                  <td id="referenceNumber">{payment.referenceNumber}</td>
                </tr>

                <tr style={{ verticalAlign: 'top' }}>
                  <td style={{ paddingBottom: '36px' }}>Beneficiary number</td>
                  <td id="beneficiaryNumber">{payment.beneficiaryNumber}</td>
                </tr>
              </Fragment>
            )}

            {payment.isCheckDelivery && (
              <Fragment>
                <tr style={{ verticalAlign: 'top' }}>
                  <td>Cheque number:</td>
                  <td id="chequeNumber">{payment.chequeNumber}</td>
                </tr>
                <tr style={{ verticalAlign: 'top' }}>
                  <td style={{ paddingBottom: '36px' }}>
                    Cheque clearing date:
                  </td>
                  <td id="chequeClearingDate">{payment.chequeClearingDate}</td>
                </tr>

                <tr style={{ verticalAlign: 'top' }}>
                  <td>Tracking number:</td>
                  <td id="trackingNumber">{payment.trackingNumber}</td>
                </tr>
                <tr style={{ verticalAlign: 'top' }}>
                  <td>Delivered to:</td>
                  <td id="deliveredTo">{payment.deliveredTo}</td>
                </tr>

                <tr style={{ verticalAlign: 'top' }}>
                  <td>Date delivered:</td>
                  <td id="deliveredDate">{payment.deliveredDate}</td>
                </tr>
                <tr style={{ verticalAlign: 'top' }}>
                  <td>Received by:</td>
                  <td id="receivedBy">{payment.receivedBy}</td>
                </tr>
                <tr style={{ verticalAlign: 'top' }}>
                  <td style={{ paddingBottom: '36px' }}>Relationship:</td>
                  <td id="relationship">{payment.relationship}</td>
                </tr>
              </Fragment>
            )}

            {/* Row 4 */}
            <tr style={{ verticalAlign: 'top' }}>
              <td>Gross payment amount:</td>
              <td id="grossAmount">{payment.grossAmount}</td>
            </tr>

            <tr style={{ verticalAlign: 'top' }}>
              <td>VAT (12%):</td>
              <td id="vat">{payment.vat}</td>
            </tr>

            <tr style={{ verticalAlign: 'top' }}>
              <td>Withholding tax (5%):</td>
              <td id="withholdingTax">{payment.withholdingTax}</td>
            </tr>

            <tr style={{ verticalAlign: 'top' }}>
              <td style={{ paddingBottom: '36px' }}>Net amount paid:</td>
              <td id="netAmount">{payment.netAmount}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: 'inline-block' }}>
          <ButtonDownload
            underline
            variant="link"
            extentions={['xlsx', 'csv']}
            onClick={(type) => onDownloadPaymentBreakdownClick(type)}
          >
            Download payment breakdown
          </ButtonDownload>
        </div>

        <div
          style={{
            borderBottom: '1px dashed #707070',
            margin: '30px 0',
          }}
        ></div>

        <PaymentPOP payment={payment} />
      </Loading>
    </Box>
  )
}

PaymentBreakdown.propTypes = propTypes
PaymentBreakdown.defaultProps = defaultProps

export default PaymentBreakdown
